"use client";

import { useEffect, useState } from "react";
import clsx from "clsx";
import { BrowserFrame } from "@/components/scene/BrowserFrame";
import { advanceScreen } from "@/lib/presenter/advanceScreen";
import { useTranslations } from "@/lib/compat/i18n";

/**
 * Screen 0.1 — Al Madar's discovery of Aurora, in two phases.
 *
 *   Phase A · ~2.5 s — Off-platform context (ABCC referral chip) + Google
 *                      search results with Aurora's verified listing as the
 *                      top organic hit. A brief click-ripple animates on
 *                      the Aurora link to telegraph the next beat.
 *
 *   Phase B · resting state — Aurora's minimal public profile (sparse:
 *                      ID + verified badge + 1-line listing) with a
 *                      "Contact us" CTA. N advances to 0.2 (channel picker).
 *
 *   The transition is auto — presenters don't need a second N to land on
 *   the profile. Cumulative beat reads as: "Al Madar googled, clicked,
 *   landed."
 */
export function AlMadarDiscovery({
  scenarioRunning = false,
  className,
}: {
  /** True only when a scenario has been triggered from the presenter
   *  chrome (SSE event stream is non-empty). Until that happens, the
   *  discovery view stays on the search-results phase forever — the
   *  click-through + minimal-profile transition only fires for live
   *  demo runs, not for idle landings on /rundemo. */
  scenarioRunning?: boolean;
  className?: string;
}) {
  const t = useTranslations("alMadarDiscovery");
  const [phase, setPhase] = useState<"search" | "profile">("search");
  const [clicked, setClicked] = useState(false);

  useEffect(() => {
    if (!scenarioRunning) return;
    const t1 = setTimeout(() => setClicked(true), 2600);
    const t2 = setTimeout(() => setPhase("profile"), 3500);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [scenarioRunning]);

  if (phase === "profile") {
    return <MinimalPublicProfile className={className} />;
  }

  return (
    <div className={clsx("grid h-full grid-cols-5 gap-4 scene-fade", className)}>
      {/* Left — ABCC referral, off-platform context */}
      <div className="glass col-span-2 flex flex-col gap-4 p-5">
        <div>
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">
            {t("referralKicker")}
          </p>
          <h3 className="mt-1 font-display text-base font-semibold text-orbitra-paper">
            {t("referralTitle")}
          </h3>
          <p className="mt-1 text-[11px] text-orbitra-muted">
            {t("referralSubtitle")}
          </p>
        </div>

        <div className="flex flex-col gap-2 rounded-2xl border border-state-verified/20 bg-orbitra-ink/60 p-4">
          <div className="flex items-center gap-3 border-b border-state-verified/15 pb-2">
            <span className="grid h-9 w-9 place-items-center rounded-full bg-state-verified text-[11px] font-semibold text-orbitra-ink">RT</span>
            <div className="flex min-w-0 flex-col">
              <span className="text-[12px] font-semibold text-orbitra-paper">{t("liaisonName")}</span>
              <span className="text-[10px] text-orbitra-muted">{t("liaisonRole")}</span>
            </div>
            <span className="ms-auto rounded-full bg-state-verified/15 px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider text-state-verified">
              {t("whatsappChip")}
            </span>
          </div>

          <div className="max-w-[420px] rounded-2xl rounded-bl-sm bg-state-verified/10 p-3 text-[12px] text-orbitra-paper">
            <p>
              {t("referralMessage").split("{aurora}").flatMap((part, i) =>
                i === 0
                  ? [part, <span key="aurora" className="font-semibold text-state-verified">{t("referralMessageAurora")}</span>]
                  : [part]
              )}
            </p>
            <p className="mt-2 text-[9px] text-orbitra-muted">{t("referralTime")}</p>
          </div>
        </div>

        <p className="mt-auto text-[10px] text-orbitra-muted italic">
          {t("offPlatformNote")}
        </p>
      </div>

      {/* Right — Google search results */}
      <div className="col-span-3 flex flex-col gap-3">
        <BrowserFrame url={`google.com/search?q=${encodeURIComponent(t("searchQueryLabel"))}`}>
          <div className="flex flex-col gap-4 p-5">
            <div className="flex items-center gap-2 rounded-full border border-state-verified/20 bg-orbitra-ink/40 px-4 py-2">
              <span className="font-display text-[14px] text-orbitra-muted">G</span>
              <span className="flex-1 text-[12px] text-orbitra-paper">{t("searchQueryLabel")}</span>
              <span className="font-mono text-[10px] text-orbitra-muted">{t("searchResultsMeta")}</span>
            </div>

            {/* Top organic result with click-ripple animation. */}
            <a
              className={clsx(
                "relative block rounded-md border border-state-verified/30 bg-state-verified/5 p-3 transition-all duration-300",
                clicked
                  ? "scale-[0.985] border-state-verified bg-state-verified/15 shadow-[0_0_30px_var(--state-verified-glow)]"
                  : "shadow-[0_0_18px_var(--state-verified-glow)] animate-pulse",
              )}
            >
              <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-verified">{t("verifiedSellerChip")}</p>
              <p className="mt-1 font-display text-[13px] font-semibold text-orbitra-paper">
                {t("listingTitle")}
              </p>
              <p className="mt-1 text-[11px] text-orbitra-teal-soft">
                {t("listingSummary")}
              </p>
              <p className="mt-2 font-mono text-[10px] text-orbitra-muted">
                {t("listingUrl")}
              </p>
              {clicked && (
                <span
                  aria-hidden="true"
                  className="absolute inset-0 flex items-center justify-center rounded-md bg-state-verified/8 backdrop-blur-[1px]"
                >
                  <span className="rounded-full bg-state-verified/30 px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-orbitra-paper">
                    {t("openingLabel")}
                  </span>
                </span>
              )}
            </a>

            <div className="flex flex-col gap-2 text-[10px] text-orbitra-muted">
              <div className="rounded-md border border-orbitra-muted/20 bg-orbitra-ink/30 p-2">
                <p className="text-orbitra-paper">{t("wikiTitle")}</p>
                <p className="mt-0.5 truncate">{t("wikiSummary")}</p>
              </div>
              <div className="rounded-md border border-orbitra-muted/20 bg-orbitra-ink/30 p-2">
                <p className="text-orbitra-paper">{t("newsletterTitle")}</p>
                <p className="mt-0.5 truncate">{t("newsletterSummary")}</p>
              </div>
            </div>
          </div>
        </BrowserFrame>

        <div className="glass flex items-center gap-3 px-4 py-2 text-[11px]">
          <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-verified">{t("knowledgePanelKicker")}</span>
          <span className="text-orbitra-paper">{t("auroraAvesBrasilSA")}</span>
          <span className="text-orbitra-muted">·</span>
          <span className="text-orbitra-teal-soft">corbid:5493-aves-aurora-br</span>
          <span className="ms-auto rounded-full border border-state-verified/40 bg-state-verified/10 px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider text-state-verified">
            {t("verifiedTraderChip")}
          </span>
        </div>
      </div>
    </div>
  );
}

export function MinimalPublicProfile({ className }: { className?: string }) {
  const t = useTranslations("alMadarDiscovery");
  return (
    <div className={clsx("h-full scene-fade", className)}>
      <BrowserFrame url="orbitra.trade/c/aurora-aves">
        <div className="grid h-full place-items-center p-6">
          <div className="w-full max-w-2xl">
            <div className="flex items-center gap-4">
              <span className="grid h-14 w-14 place-items-center rounded-full bg-state-verified/15 font-display text-[14px] font-semibold text-state-verified">AA</span>
              <div className="flex flex-col">
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-state-verified">
                  {t("profileKicker")}
                </p>
                <p className="font-display text-2xl font-semibold text-orbitra-paper">{t("auroraAvesBrasilSA")}</p>
                <p className="font-mono text-[11px] text-orbitra-muted">corbid:5493-aves-aurora-br · BR</p>
              </div>
              <span className="ms-auto rounded-full border border-state-verified/40 bg-state-verified/10 px-3 py-1 font-display text-[11px] font-semibold uppercase tracking-wider text-state-verified shadow-[0_0_18px_var(--state-verified-glow)]">
                {t("verifiedTraderBadge")}
              </span>
            </div>

            <div className="mt-6 rounded-md border border-state-verified/30 bg-orbitra-ink/40 px-4 py-3">
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-orbitra-teal-soft">{t("profileListingKicker")}</p>
              <p className="mt-1 font-display text-base font-semibold text-orbitra-paper">
                {t("profileListingTitle")}
              </p>
              <p className="mt-1 text-[11px] text-orbitra-muted">
                {t("profileListingBody")}
              </p>
            </div>

            <div className="mt-6 flex items-center justify-between rounded-md border border-state-verified/40 bg-state-verified/10 px-5 py-4">
              <p className="text-[12px] text-orbitra-paper">
                {t("contactPrompt")}
              </p>
              <button type="button" onClick={advanceScreen} className="cta-primary px-5 py-2.5 font-display text-sm font-semibold">
                {t("contactCta")}
              </button>
            </div>

            <p className="mt-4 text-center text-[10px] text-orbitra-muted">
              {t("profileFooter")}
            </p>
          </div>
        </div>
      </BrowserFrame>
    </div>
  );
}
